import { STAGES, resolveStatus, statusToStage } from '@constants/applicationStatus';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import {
  getActiveHrAssignmentsQuery,
  getAllUsersForNames,
  getRecruiterProductivityQuery,
  premiumEmployers,
  workbookApplicationsQuery,
  workbookJobsQuery,
  workbookTalentPoolQuery,
} from '@constants/graphQLQueries';

/**
 * Multi-sheet "Submissions" workbook export — the system-generated counterpart to the team's
 * hand-maintained `2026 Submissions Mintly.xlsx`. Builds seven sheets (Submissions, Shortlist,
 * HR Filters, Primary/Secondary, Workable Demands, Recruiter Productivity, Talent Pool) from
 * managed-hiring data already in the platform. ExcelJS is lazy-loaded (~940 kB) so only a Super
 * Admin clicking Export pays for it.
 */

const HEADER_BG = '0369A1'; // --color-primary (deep ocean blue)
const TITLE_TEXT = '0C2A3A'; // near-black ink
const SUBTITLE_TEXT = '5B7488'; // muted slate
const BORDER = 'D9E4EF'; // soft border
const ZEBRA = 'F4F9FD'; // very light blue band for alternate rows

/** Stage token -> {bg,text} chip colours, matching the UI + the applications export. */
const STAGE_HEX: Record<string, { bg: string; text: string }> = {
  qualified: { bg: 'E0F2FE', text: '0369A1' }, // Qualified, Submitted
  lead: { bg: 'EEF2FF', text: '4338CA' }, // Interested, Screening
  proposal: { bg: 'FEF3C7', text: 'B45309' }, // Interview
  negotiation: { bg: 'FFEDD5', text: 'C2410C' }, // Offer
  won: { bg: 'D1FAE5', text: '065F46' }, // Joined
  lost: { bg: 'FEE2E2', text: '991B1B' }, // Closed
  muted: { bg: 'F5F5F5', text: '757575' }, // Sourcing, Contacted
};

// Stage label -> STAGE_HEX token, from the one stage list the UI uses.
const STAGE_TOKEN: Record<string, string> = Object.fromEntries(STAGES.map((s) => [s.label, s.token]));

const colorsForStage = (stage?: string): { bg: string; text: string } | null => {
  if (!stage) return null;
  return STAGE_HEX[STAGE_TOKEN[stage] ?? ''] ?? null;
};
const colorsForStatus = (status?: string): { bg: string; text: string } | null =>
  colorsForStage(statusToStage(status ?? ''));

/*
 * Bounded fetches.
 *
 * The caps keep the export snappy while the managed book is small. The comment here used to
 * claim they were "logged if hit" and nothing compared the returned count against the total, so
 * an export that dropped rows looked exactly like one that did not. That is the dangerous
 * version of this: a workbook someone reconciles numbers from, silently short. `truncationNotes`
 * below is what makes it true.
 */
const APP_CAP = 5000;
const JOB_CAP = 2000;
const TALENT_CAP = 5000;
const CLIENT_CAP = 500;

// A closure (the "Shortlist" sheet): at Offer, from Shortlisted on, or Joined. "Offer pending"
// is left out, as the old list left it out.
const CLOSURE_DETAILS = ['Shortlisted', 'Selected', 'Offer sent', 'Offer accepted'];
const isClosure = (a: any): boolean => {
  const r = resolveStatus(a.status, a.subStatus);
  return r.stage?.label === 'Joined' || (r.stage?.label === 'Offer' && CLOSURE_DETAILS.includes(r.detail));
};

/** Detail for an open record, reason for a closed one. */
const detailOrReason = (a: any): string => {
  const r = resolveStatus(a.status, a.subStatus, a.closedReason);
  if (r.stage?.label === 'Closed') return r.closedReason;
  return r.detail && r.detail !== r.stage?.label ? r.detail : '';
};
const OPEN_JOB = new Set(['open', 'active']);
const FILLED_JOB = new Set(['closed', 'filled', 'fulfilled', 'completed']);

type Col = { header: string; width: number };

const short = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '' : String(v);

const monthOf = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
};

const asDate = (iso?: string): Date | string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d;
};

const positions = (j: any): number => Math.max(1, Number(j?.numberOfPositions) || 1);

const salaryRange = (j: any): string => {
  const cur = j?.salaryCurrency || '';
  if (j?.salaryMin || j?.salaryMax) {
    const lo = j?.salaryMin ? Number(j.salaryMin).toLocaleString('en-IN') : '';
    const hi = j?.salaryMax ? Number(j.salaryMax).toLocaleString('en-IN') : '';
    return [lo && hi ? `${lo} to ${hi}` : lo || hi, cur].filter(Boolean).join(' ');
  }
  return short(j?.salary);
};

const expectedSalary = (t: any): string => {
  const p = t?.jobPreferences;
  if (p?.salaryExpectationMin || p?.salaryExpectationMax) {
    const lo = p.salaryExpectationMin ? Number(p.salaryExpectationMin).toLocaleString('en-IN') : '';
    const hi = p.salaryExpectationMax ? Number(p.salaryExpectationMax).toLocaleString('en-IN') : '';
    return [lo && hi ? `${lo} to ${hi}`.trim() : lo || hi, p.salaryExpectationCurrency || '']
      .filter(Boolean)
      .join(' ');
  }
  return '';
};

const latestNote = (notes?: any[]): string => {
  if (!notes?.length) return '';
  const last = [...notes].reverse().find((n) => n?.note);
  return short(last?.note);
};

const latestFeedback = (feedback?: any[]): string => {
  if (!feedback?.length) return '';
  const last = [...feedback].reverse()[0];
  const rating = last?.rating ? `${last.rating}/5` : '';
  return [rating, short(last?.comment)].filter(Boolean).join(' - ');
};

/** Find an internalFilters value by (case-insensitive) key. */
const pickFilter = (filters: any[] | undefined, key: string): string => {
  const hit = (filters ?? []).find(
    (f) => String(f?.key ?? '').toLowerCase() === key.toLowerCase(),
  );
  return short(hit?.value);
};

type SheetOpts = {
  subtitle?: string;
  /** Tab colour (ARGB hex, no leading FF). */
  tabColor?: string;
  /** 1-based columns to tint by the row's application status/stage value. */
  statusCols?: number[];
  /** 1-based columns holding Date values, formatted `dd mmm yyyy`. */
  dateCols?: number[];
  /** 1-based columns whose text should wrap (e.g. comments). */
  wrapCols?: number[];
};

/**
 * Adds a polished worksheet: a title + subtitle band, a bold blue frozen header, zebra rows,
 * autofilter, a coloured tab, and optional status-tinted / date-formatted / wrapped columns.
 */
function addSheet(
  workbook: any,
  title: string,
  columns: Col[],
  rows: any[][],
  opts: SheetOpts = {},
): void {
  const n = columns.length;
  const sheet = workbook.addWorksheet(title, {
    views: [{ state: 'frozen', ySplit: opts.subtitle ? 3 : 2 }],
  });
  if (opts.tabColor) sheet.properties.tabColor = { argb: `FF${opts.tabColor}` };

  // Title band
  const titleRow = sheet.addRow([title]);
  titleRow.font = { bold: true, size: 15, color: { argb: `FF${TITLE_TEXT}` } };
  titleRow.height = 24;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, n);
  if (opts.subtitle) {
    const sub = sheet.addRow([opts.subtitle]);
    sub.font = { italic: true, size: 10, color: { argb: `FF${SUBTITLE_TEXT}` } };
    sheet.mergeCells(sub.number, 1, sub.number, n);
  }

  // Header
  const headerRow = sheet.addRow(columns.map((c) => c.header));
  headerRow.eachCell((cell: any) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_BG}` } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = { bottom: { style: 'thin', color: { argb: `FF${HEADER_BG}` } } };
  });
  headerRow.height = 22;
  columns.forEach((c, i) => {
    sheet.getColumn(i + 1).width = c.width;
  });

  const headerNo = headerRow.number;
  const statusSet = new Set(opts.statusCols ?? []);
  const dateSet = new Set(opts.dateCols ?? []);
  const wrapSet = new Set(opts.wrapCols ?? []);

  rows.forEach((r, ri) => {
    const row = sheet.addRow(r);
    row.alignment = { vertical: 'middle' };
    if (ri % 2 === 1) {
      row.eachCell({ includeEmpty: true }, (cell: any) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${ZEBRA}` } };
      });
    }
    row.eachCell({ includeEmpty: true }, (cell: any, col: number) => {
      cell.border = { bottom: { style: 'hair', color: { argb: `FF${BORDER}` } } };
      if (dateSet.has(col)) cell.numFmt = 'dd mmm yyyy';
      if (wrapSet.has(col)) cell.alignment = { vertical: 'middle', wrapText: true };
      if (statusSet.has(col)) {
        const colors = colorsForStatus(String(cell.value ?? ''));
        if (colors) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${colors.bg}` } };
          cell.font = { color: { argb: `FF${colors.text}` }, bold: true };
        }
      }
    });
  });

  if (rows.length) {
    sheet.autoFilter = {
      from: { row: headerNo, column: 1 },
      to: { row: headerNo, column: n },
    };
  }
}

/** A cover/contents sheet: brand title, generated timestamp, and a per-sheet row-count index. */
function addCoverSheet(
  workbook: any,
  generatedOn: string,
  index: { name: string; rows: number; note: string }[],
): void {
  const sheet = workbook.addWorksheet('Overview', {
    views: [{ state: 'frozen', ySplit: 0 }],
  });
  sheet.properties.tabColor = { argb: `FF${HEADER_BG}` };
  sheet.getColumn(1).width = 30;
  sheet.getColumn(2).width = 12;
  sheet.getColumn(3).width = 60;

  const brand = sheet.addRow(['Mintly Submissions Workbook']);
  brand.font = { bold: true, size: 18, color: { argb: `FF${HEADER_BG}` } };
  brand.height = 28;
  sheet.mergeCells(brand.number, 1, brand.number, 3);

  const gen = sheet.addRow([`Generated ${generatedOn} · Premium Hiring book`]);
  gen.font = { italic: true, size: 11, color: { argb: `FF${SUBTITLE_TEXT}` } };
  sheet.mergeCells(gen.number, 1, gen.number, 3);

  sheet.addRow([]);
  const hdr = sheet.addRow(['Sheet', 'Rows', 'What it covers']);
  hdr.eachCell((cell: any) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_BG}` } };
  });
  index.forEach((it, i) => {
    const row = sheet.addRow([it.name, it.rows, it.note]);
    if (i % 2 === 1) {
      row.eachCell({ includeEmpty: true }, (cell: any) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${ZEBRA}` } };
      });
    }
    row.getCell(3).alignment = { wrapText: true, vertical: 'middle' };
  });
}

/**
 * Fetches all managed-hiring data and downloads the multi-sheet workbook.
 * Super-Admin only (the caller gates the button); every sheet is drawn from the managed book.
 */
export async function exportSubmissionsWorkbook(): Promise<string[]> {
  const ExcelJS = (await import('exceljs')).default;

  const managedApps = ['{ key: "job.hiringMode", value: "MANAGED" }'];
  const managedJobs = ['{ key: "hiringMode", value: "MANAGED" }'];
  const today = new Date().toISOString().slice(0, 10);

  const [empRes, appRes, jobRes, talentRes, recruiterRes, usersRes] = await Promise.all([
    fetchGraphQLApiData(premiumEmployers(0, CLIENT_CAP)).catch(() => null),
    fetchGraphQLApiData(workbookApplicationsQuery(0, APP_CAP, managedApps)).catch(() => null),
    fetchGraphQLApiData(workbookJobsQuery(0, JOB_CAP, managedJobs)).catch(() => null),
    fetchGraphQLApiData(workbookTalentPoolQuery(0, TALENT_CAP, [])).catch(() => null),
    fetchGraphQLApiData(getRecruiterProductivityQuery({ from: '2024-01-01', to: today })).catch(
      () => null,
    ),
    fetchGraphQLApiData(getAllUsersForNames).catch(() => null),
  ]);

  const employers: any[] = empRes?.data?.getAllEmployers?.content ?? [];
  const apps: any[] = appRes?.data?.getAllJobApplications?.content ?? [];
  const jobs: any[] = jobRes?.data?.getAllJobs?.content ?? [];
  const talent: any[] = talentRes?.data?.getAllJobSeekers?.content ?? [];
  const recruiters: any[] = recruiterRes?.data?.getRecruiterProductivity ?? [];
  const users: any[] = usersRes?.data?.getAllUsers?.content ?? [];

  /*
   * What this workbook does NOT contain.
   *
   * Each sheet is one page-0 request with a cap, so past that cap the export is the newest N and
   * the rest are absent. Staging will never show this; production will. A row count that is
   * quietly short is worse than a failed download, because nothing about the file says to
   * distrust it.
   */
  const truncationNotes: string[] = [];
  const checkCoverage = (label: string, got: number, total?: number, cap?: number) => {
    if (typeof total === 'number' && cap && total > cap) {
      const note = `${label}: ${got.toLocaleString()} of ${total.toLocaleString()} exported. ` +
        `Capped at ${cap.toLocaleString()}, newest first.`;
      truncationNotes.push(note);
      console.warn('[workbook] ' + note);
    }
  };
  checkCoverage('Applications', apps.length, appRes?.data?.getAllJobApplications?.totalElements, APP_CAP);
  checkCoverage('Jobs', jobs.length, jobRes?.data?.getAllJobs?.totalElements, JOB_CAP);
  checkCoverage('Talent pool', talent.length, talentRes?.data?.getAllJobSeekers?.totalElements, TALENT_CAP);
  checkCoverage('Clients', employers.length, empRes?.data?.getAllEmployers?.totalElements, CLIENT_CAP);

  // id -> display name for HR/recruiter columns.
  const nameById: Record<string, string> = {};
  users.forEach((u) => {
    const n =
      u?.personalDetails?.fullName ||
      [u?.personalDetails?.firstName, u?.personalDetails?.lastName].filter(Boolean).join(' ');
    if (u?._id && n) nameById[u._id] = n;
  });
  const hrNames = (ids?: string[]): string =>
    ids?.length ? ids.map((id) => nameById[id] || id).join(', ') : '';

  // Primary/secondary owner per client (N+1 over the small managed-client set).
  const ownerByClient: Record<string, { primary?: string; secondary?: string }> = {};
  await Promise.all(
    employers.map(async (e) => {
      const r = await fetchGraphQLApiData(getActiveHrAssignmentsQuery(e._id)).catch(() => null);
      const rows: any[] = r?.data?.getActiveHrAssignments ?? [];
      const entry: { primary?: string; secondary?: string } = {};
      rows.forEach((a) => {
        const name = nameById[a.hrUserId] || a.hrUserId;
        if (String(a.role).toUpperCase() === 'SECONDARY') entry.secondary = name;
        else entry.primary = name;
      });
      ownerByClient[e._id] = entry;
    }),
  );
  // companyName -> primary owner, for the job-level "Team" column.
  const ownerByCompany: Record<string, string> = {};
  employers.forEach((e) => {
    const cn = e?.company?.companyName;
    if (cn) ownerByCompany[cn] = ownerByClient[e._id]?.primary ?? '';
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Mintly Control Center';
  workbook.created = new Date();

  const closures = apps.filter(isClosure);
  const openJobs = jobs.filter((j) => OPEN_JOB.has(String(j.jobStatus ?? '').toLowerCase()));
  const generatedOn = new Date().toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  // 0) Cover / contents
  addCoverSheet(workbook, generatedOn, [
    { name: 'Submissions', rows: apps.length, note: 'Every Premium Hiring application: candidate, client, status, HR, comments and feedback.' },
    { name: 'Shortlist', rows: closures.length, note: 'Shortlisted, selected and joined candidates (closures).' },
    { name: 'HR Filters', rows: jobs.length, note: 'Client screening filters per demand: gender, religion, skills, salary, location.' },
    { name: 'PrimarySecondary', rows: employers.length, note: 'Primary and secondary account owners, with open vs filled roles per client.' },
    { name: 'Workable Demands', rows: openJobs.length, note: 'Open Premium Hiring roles: positions, salary, skills, sales owner and HR team.' },
    { name: 'Recruiter Productivity', rows: recruiters.length, note: 'Submissions, hires and credited hires per recruiter.' },
    { name: 'Talent Pool', rows: talent.length, note: 'Candidate pool including externally-sourced (external vs platform).' },
  ]);

  // 1) Submissions
  addSheet(
    workbook,
    'Submissions',
    [
      { header: 'Applied On', width: 18 },
      { header: 'Client', width: 24 },
      { header: 'Position', width: 26 },
      { header: 'Candidate', width: 24 },
      { header: 'Contact Number', width: 16 },
      { header: 'Stage', width: 16 },
      { header: 'Detail or reason', width: 22 },
      { header: 'HR', width: 22 },
      { header: 'Comments', width: 40 },
      { header: 'Feedback', width: 30 },
    ],
    apps.map((a) => [
      asDate(a.createdAt),
      short(a.job?.companyName),
      short(a.job?.title),
      short(a.jobSeeker?.personalDetails?.fullName),
      short(a.jobSeeker?.personalDetails?.phone),
      short(statusToStage(a.status) || a.status),
      short(detailOrReason(a)),
      hrNames(a.assignedHRs),
      latestNote(a.notes),
      latestFeedback(a.feedback),
    ]),
    {
      subtitle: 'Every Premium Hiring application, newest first.',
      tabColor: '0369A1',
      dateCols: [1],
      statusCols: [6, 7],
      wrapCols: [9, 10],
    },
  );

  // 2) Shortlist / Closures
  addSheet(
    workbook,
    'Shortlist',
    [
      { header: '#', width: 6 },
      { header: 'Month', width: 12 },
      { header: 'Name', width: 24 },
      { header: 'Company', width: 24 },
      { header: 'Designation', width: 26 },
      { header: 'City', width: 16 },
      { header: 'Status', width: 16 },
      { header: 'Team', width: 22 },
      { header: 'Source', width: 14 },
    ],
    closures.map((a, i) => [
      i + 1,
      monthOf(a.createdAt),
      short(a.jobSeeker?.personalDetails?.fullName),
      short(a.job?.companyName),
      short(a.job?.title),
      short(a.jobSeeker?.personalDetails?.city),
      short(detailOrReason(a) || statusToStage(a.status)),
      hrNames(a.assignedHRs),
      a.jobSeeker?.isExternal ? 'External' : 'Platform',
    ]),
    {
      subtitle: 'Shortlisted, selected and joined candidates.',
      tabColor: '065F46',
      statusCols: [7],
    },
  );

  // 3) HR Filters (Jobs)
  addSheet(
    workbook,
    'HR Filters',
    [
      { header: 'Client', width: 24 },
      { header: 'Filter 1', width: 24 },
      { header: 'Filter 2', width: 24 },
      { header: 'Filter 3', width: 24 },
      { header: 'Location', width: 20 },
      { header: 'Salary Range', width: 20 },
      { header: 'Gender', width: 12 },
      { header: 'Religion', width: 14 },
      { header: 'Position', width: 26 },
    ],
    jobs.map((j) => {
      const filters: any[] = j.internalFilters ?? [];
      const rest = filters.filter(
        (f) => !['gender', 'religion'].includes(String(f?.key ?? '').toLowerCase()),
      );
      const asFilter = (f: any) => (f ? `${short(f.key)}: ${short(f.value)}` : '');
      return [
        short(j.companyName),
        asFilter(rest[0]),
        asFilter(rest[1]),
        asFilter(rest[2]),
        short(j.location || j.city),
        salaryRange(j),
        pickFilter(filters, 'gender') || short(j.gender),
        pickFilter(filters, 'religion'),
        short(j.title),
      ];
    }),
    { subtitle: 'Internal client screening filters per demand.', tabColor: 'B45309' },
  );

  // 4) Primary / Secondary
  addSheet(
    workbook,
    'PrimarySecondary',
    [
      { header: 'Client', width: 26 },
      { header: 'Industry', width: 18 },
      { header: 'Open Roles', width: 12 },
      { header: 'Roles Filled', width: 12 },
      { header: 'Primary Owner', width: 22 },
      { header: 'Secondary Owner', width: 22 },
    ],
    employers.map((e) => {
      const cn = e?.company?.companyName ?? '';
      const clientJobs = jobs.filter((j) => j.companyName === cn);
      // Industry isn't on the employer record; derive it from the client's jobs.
      const industry = short(clientJobs.find((j) => j.industry)?.industry);
      const openRoles = clientJobs
        .filter((j) => OPEN_JOB.has(String(j.jobStatus ?? '').toLowerCase()))
        .reduce((s, j) => s + positions(j), 0);
      const filledRoles = clientJobs
        .filter((j) => FILLED_JOB.has(String(j.jobStatus ?? '').toLowerCase()))
        .reduce((s, j) => s + positions(j), 0);
      const owner = ownerByClient[e._id] ?? {};
      return [cn, industry, openRoles, filledRoles, owner.primary ?? '', owner.secondary ?? ''];
    }),
    { subtitle: 'Account owners and open vs filled roles per client.', tabColor: '4338CA' },
  );

  // 5) Workable Demands (managed open jobs)
  addSheet(
    workbook,
    'Workable Demands',
    [
      { header: 'Month', width: 12 },
      { header: 'Client', width: 24 },
      { header: 'Location', width: 20 },
      { header: 'Skills', width: 30 },
      { header: '# of Positions', width: 14 },
      { header: 'Salary', width: 20 },
      { header: 'Data Available', width: 14 },
      { header: 'Team', width: 22 },
      { header: 'Sales', width: 20 },
    ],
    openJobs.map((j) => [
      monthOf(j.createdAt),
      short(j.companyName),
      short(j.location || j.city),
      (j.skillsRequired ?? []).join(', '),
      positions(j),
      salaryRange(j),
      (j.relevantCandidates?.length ?? 0) > 0 ? 'Yes' : 'No',
      ownerByCompany[j.companyName] ?? '',
      short(j.salesOwnerName),
    ]),
    { subtitle: 'Open Premium Hiring roles awaiting candidates.', tabColor: 'C2410C', wrapCols: [4] },
  );

  // 6) Recruiter Productivity
  addSheet(
    workbook,
    'Recruiter Productivity',
    [
      { header: 'Team Member', width: 24 },
      { header: 'Jobs', width: 10 },
      { header: 'Candidates', width: 12 },
      { header: 'Interviews', width: 12 },
      { header: 'Offers', width: 10 },
      { header: 'Hires', width: 10 },
      { header: 'Credited Hires', width: 14 },
      { header: 'Avg Days to Shortlist', width: 18 },
      { header: 'Avg Days to Hire', width: 16 },
    ],
    recruiters.map((r) => [
      nameById[r.recruiterId] || r.recruiterId,
      Number(r.jobsHandled) || 0,
      Number(r.candidatesHandled) || 0,
      Number(r.interviews) || 0,
      Number(r.offersMade) || 0,
      Number(r.hires) || 0,
      r.creditedHires != null ? Number(r.creditedHires.toFixed(1)) : 0,
      r.avgDaysToShortlist != null ? Number(r.avgDaysToShortlist.toFixed(1)) : '',
      r.avgDaysToHire != null ? Number(r.avgDaysToHire.toFixed(1)) : '',
    ]),
    { subtitle: 'Per-recruiter output; credited hires split shared closures 1/N.', tabColor: '0D9488' },
  );

  // 7) Talent Pool (CAD-style pools + external candidates)
  addSheet(
    workbook,
    'Talent Pool',
    [
      { header: 'Name', width: 24 },
      { header: 'Phone', width: 16 },
      { header: 'City', width: 16 },
      { header: 'Current Company', width: 22 },
      { header: 'Current Salary', width: 16 },
      { header: 'Expected Salary', width: 18 },
      { header: 'Experience', width: 14 },
      { header: 'Skills', width: 30 },
      { header: 'Source', width: 12 },
      { header: 'Notice Period', width: 14 },
    ],
    talent.map((t) => [
      short(t.personalDetails?.fullName),
      short(t.personalDetails?.phone),
      short(t.personalDetails?.city || t.currentLocation),
      short(t.currentCompany),
      short(t.currentSalary),
      expectedSalary(t),
      short(t.yearsOfExperience ?? t.experience),
      (t.skills ?? []).join(', '),
      t.isExternal ? 'External' : 'Platform',
      short(t.jobPreferences?.noticePeriod),
    ]),
    { subtitle: 'Sourced candidate pool (external and platform).', tabColor: '7C3AED', wrapCols: [8] },
  );

  /*
   * The coverage sheet goes first, so it is the sheet the file opens on.
   *
   * A note buried on the last tab is a note nobody reads before pasting numbers into a deck.
   * When nothing was capped this sheet is not added at all: a permanent "everything is fine"
   * tab trains people to ignore it.
   */
  if (truncationNotes.length) {
    const sheet = workbook.addWorksheet('Coverage', { properties: { tabColor: { argb: 'B91C1C' } } });
    sheet.addRow(['This workbook is incomplete']).font = { bold: true, size: 14 };
    sheet.addRow([]);
    truncationNotes.forEach((n) => sheet.addRow([n]));
    sheet.addRow([]);
    sheet.addRow([
      'Filters applied on screen do not affect this export: it always covers the Premium Hiring book.',
    ]);
    sheet.getColumn(1).width = 110;
    workbook.worksheets.splice(workbook.worksheets.indexOf(sheet), 1);
    workbook.worksheets.unshift(sheet);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mintly-submissions-workbook-${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);

  // Returned so the caller can warn on screen too. The sheet is the durable record; a toast is
  // what stops someone acting on the file before they open it.
  return truncationNotes;
}
